import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRightIcon,
  BellAlertIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { Link, Tooltip } from "@";
import { typeConfig, WHATS_NEW_URL } from "./whatsNewData";

const STORAGE_KEY = "whats-new-last-seen-id";

const getLastSeenId = () => {
  try {
    return parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
  } catch {
    return 0;
  }
};

const fetchWhatsNew = async () => {
  if (process.env.NODE_ENV === "development") {
    const res = await fetch("/whats-new.json");
    if (!res.ok) return [];
    return res.json();
  }

  try {
    const res = await fetch(WHATS_NEW_URL, { credentials: "omit" });
    if (res.ok) return res.json();
  } catch {
    // fallback to local
  }
  const res = await fetch("/whats-new.json");
  if (!res.ok) return [];
  return res.json();
};

// Grouping helpers
const DAY_MS = 1000 * 60 * 60 * 24;
const groupByRecency = (entries) => {
  const now = Date.now();
  const groups = {
    new: { label: "جديد", entries: [] },
    week: { label: "هذا الأسبوع", entries: [] },
    month: { label: "هذا الشهر", entries: [] },
    older: { label: "أقدم", entries: [] },
  };
  entries.forEach((entry) => {
    const ageDays = (now - new Date(entry.date).getTime()) / DAY_MS;
    if (ageDays <= 3) groups.new.entries.push(entry);
    else if (ageDays <= 7) groups.week.entries.push(entry);
    else if (ageDays <= 30) groups.month.entries.push(entry);
    else groups.older.entries.push(entry);
  });
  return groups;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("ar-SA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatRelative = (dateStr) => {
  const ageDays = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / DAY_MS,
  );
  if (ageDays === 0) return "اليوم";
  if (ageDays === 1) return "أمس";
  if (ageDays < 7) return `منذ ${ageDays} أيام`;
  if (ageDays < 30) {
    const weeks = Math.floor(ageDays / 7);
    return `منذ ${weeks} ${weeks === 1 ? "أسبوع" : "أسابيع"}`;
  }
  if (ageDays < 365) {
    const months = Math.floor(ageDays / 30);
    return `منذ ${months} ${months === 1 ? "شهر" : "أشهر"}`;
  }
  return formatDate(dateStr);
};

const WhatsNew = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [lastSeenId, setLastSeenId] = useState(getLastSeenId);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const panelRef = useRef(null);

  const { data: entries = [] } = useQuery({
    queryKey: ["whats-new"],
    queryFn: fetchWhatsNew,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: true,
  });

  const latestId = entries[0]?.id || 0;
  const unreadCount = entries?.filter((e) => e.id > lastSeenId).length;

  // Counts per type for filter pills
  const counts = useMemo(() => {
    const c = { all: entries.length };
    entries.forEach((entry) => {
      const t = entry.type || "feature";
      c[t] = (c[t] || 0) + 1;
    });
    return c;
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return entries.filter((entry) => {
      const type = entry.type || "feature";
      if (activeFilter !== "all" && type !== activeFilter) return false;
      if (!q) return true;
      const typeLabel = (typeConfig[type] || typeConfig.feature).label;
      return (
        entry.title?.toLowerCase().includes(q) ||
        entry.description?.toLowerCase().includes(q) ||
        typeLabel.toLowerCase().includes(q)
      );
    });
  }, [entries, searchQuery, activeFilter]);

  const grouped = useMemo(
    () => groupByRecency(filteredEntries),
    [filteredEntries],
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    if (latestId > 0) {
      setLastSeenId(latestId);
      localStorage.setItem(STORAGE_KEY, String(latestId));
    }
  }, [latestId]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setActiveFilter("all");
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, handleClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, handleClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);

  const filterPills = [
    { id: "all", label: "الكل", count: counts.all || 0 },
    {
      id: "feature",
      label: typeConfig.feature.label,
      count: counts.feature || 0,
    },
    {
      id: "improvement",
      label: typeConfig.improvement.label,
      count: counts.improvement || 0,
    },
    { id: "fix", label: typeConfig.fix.label, count: counts.fix || 0 },
  ].filter((p) => p.id === "all" || p.count > 0);

  return (
    <>
      {/* Trigger Button */}
      {entries.length > 0 && (
        <button
          type="button"
          onClick={handleOpen}
          data-tooltip-target="whats-new"
          data-tooltip-placement="bottom"
          aria-label="آخر التحديثات"
          className="group relative flex h-10 w-10 items-center justify-center rounded-full bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-700 transition-all duration-200 hover:bg-white hover:text-purple-900 hover:ring-purple-200 dark:hover:bg-gray-900/50 dark:hover:text-purple-300 dark:hover:ring-purple-700/40 active:scale-95"
        >
          <SparklesIcon className="h-5 w-5 transition-transform group-hover:scale-110" />
          {unreadCount > 0 && (
            <span className="absolute -end-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-red-600 px-1 text-[10px] font-bold text-white dark:text-gray-300 shadow-sm shadow-red-500/30 ring-2 ring-white dark:ring-gray-700 ">
              {unreadCount}
            </span>
          )}
        </button>
      )}
      <Tooltip
        className="dark:bg-gray-700"
        title="آخر التحديثات"
        id="whats-new"
      />

      {createPortal(
        <>
          {/* Backdrop */}
          <div
            aria-hidden
            className={`fixed inset-0 z-[999] bg-gray-900/40 backdrop-blur-sm transition-opacity duration-300 dark:bg-black/60 ${
              isOpen
                ? "pointer-events-auto opacity-100"
                : "pointer-events-none opacity-0"
            }`}
          />

          {/* Slide-out Panel — wider for comfortable reading */}
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="whats-new-title"
            className={`fixed end-0 top-0 z-[999] flex h-full w-full max-w-2xl flex-col overflow-hidden bg-[#F8F8F8] shadow-2xl shadow-purple-900/10 transition-transform duration-300 ease-out dark:bg-gray-900 dark:shadow-black/40 ${
              isOpen
                ? "translate-x-0"
                : "ltr:translate-x-full rtl:-translate-x-full"
            }`}
          >
            {/* Subtle decorative orb behind header */}
            <span
              aria-hidden
              className="pointer-events-none absolute -end-20 -top-24 h-48 w-48 rounded-full bg-gradient-to-br from-purple-300/30 to-transparent blur-3xl dark:from-purple-700/25"
            />

            {/* Panel Header */}
            <header className="relative flex items-center justify-between gap-3 border-b border-gray-100 px-6 py-5 dark:border-gray-700/60">
              <div className="flex items-center gap-3">
                <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-purple-800 to-purple-950 text-white dark:text-gray-300 shadow-md shadow-purple-900/25 ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800">
                  <SparklesIcon className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span
                      aria-hidden
                      className="absolute -end-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-rose-500 dark:border-gray-900"
                    >
                      <span className="absolute inset-0 animate-ping rounded-full bg-rose-400 opacity-60" />
                    </span>
                  )}
                </span>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-purple-700 dark:text-purple-400">
                    {unreadCount > 0
                      ? `${unreadCount} تحديث جديد`
                      : "آخر التحديثات"}
                  </p>
                  <h2
                    id="whats-new-title"
                    className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-300"
                  >
                    تحديثات خزامى
                  </h2>
                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                aria-label="إغلاق"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-100 bg-white text-gray-500 transition-all duration-200 hover:rotate-90 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 active:scale-95 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </header>

            {/* Search + Filters */}
            {entries.length > 0 && (
              <div className="relative space-y-3 border-b border-gray-100 px-6 py-4 dark:border-gray-700/60">
                <div className="relative">
                  <MagnifyingGlassIcon
                    aria-hidden
                    className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث في التحديثات…"
                    className="h-11 w-full rounded-full border border-gray-300 bg-white py-2 pe-9 ps-9 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-purple-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:placeholder-gray-500 dark:focus:border-purple-700 dark:focus:bg-gray-900 dark:focus:ring-purple-900/40"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      aria-label="مسح البحث"
                      className="absolute end-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Type filter pills */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {filterPills.map((p) => {
                    const isActive = activeFilter === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setActiveFilter(p.id)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tracking-tight transition-all duration-200 ${
                          isActive
                            ? "bg-gradient-to-br from-purple-800 to-purple-950 text-white dark:text-gray-300 shadow-sm shadow-purple-900/25 ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800"
                            : "border border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300"
                        }`}
                      >
                        {p.label}
                        <span
                          className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums ${
                            isActive
                              ? "bg-white/15 text-white dark:text-gray-300"
                              : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                          }`}
                        >
                          {p.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Entries — scrollable */}
            <div className="relative flex-1 overflow-y-auto">
              {entries.length === 0 ? (
                <EmptyState
                  icon={BellAlertIcon}
                  title="لا توجد تحديثات حالياً"
                  description="سنخبرك هنا عند إصدار جديد."
                />
              ) : filteredEntries.length === 0 ? (
                <EmptyState
                  icon={MagnifyingGlassIcon}
                  title="لا توجد نتائج مطابقة"
                  description={
                    searchQuery.trim()
                      ? `لم نعثر على تحديثات تطابق "${searchQuery.trim()}"`
                      : "جرّب تصفية مختلفة"
                  }
                />
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {Object.entries(grouped).map(([key, group]) => {
                    if (group.entries.length === 0) return null;
                    return (
                      <section key={key} className="px-6 py-5">
                        <h3 className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 dark:text-purple-400">
                          <span
                            aria-hidden
                            className="h-1.5 w-1.5 rounded-full bg-purple-500 dark:bg-purple-400"
                          />
                          {group.label}
                          <span className="ms-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-50 px-1.5 text-[10px] font-bold tabular-nums text-purple-900 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
                            {group.entries.length}
                          </span>
                        </h3>

                        <div className="space-y-4">
                          {group.entries.map((entry, index) => {
                            const isNew = entry.id > lastSeenId;
                            const config =
                              typeConfig[entry.type] || typeConfig.feature;

                            return (
                              <article
                                key={entry.id}
                                className={`group/entry relative overflow-hidden rounded-2xl border bg-white opacity-0 transition-all duration-200 hover:-translate-y-0.5 dark:bg-gray-800 ${
                                  isNew
                                    ? "border-purple-200 shadow-sm shadow-purple-900/5 dark:border-purple-800/40"
                                    : "border-gray-100 hover:border-gray-200 dark:border-gray-700/60 dark:hover:border-gray-600"
                                }`}
                                style={{
                                  animation: `whatsNewSlideIn 0.4s ease-out ${
                                    index * 50
                                  }ms forwards`,
                                }}
                              >
                                {/* Start-side accent for new entries */}
                                {isNew && (
                                  <span
                                    aria-hidden
                                    className="pointer-events-none absolute start-0 top-0 h-full w-1 bg-gradient-to-b from-purple-500 to-purple-700 dark:from-purple-400 dark:to-purple-600"
                                  />
                                )}

                                {entry.image && (
                                  <a
                                    href={entry.image}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group/img relative block cursor-zoom-in overflow-hidden border-b border-gray-100 dark:border-gray-700/60"
                                  >
                                    <img
                                      src={entry.image}
                                      alt={entry.title}
                                      className="h-44 w-full object-cover transition-transform duration-500 group-hover/img:scale-105"
                                    />
                                    <span
                                      aria-hidden
                                      className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover/img:opacity-100"
                                    />
                                  </a>
                                )}

                                <div className="p-5">
                                  {/* Meta strip — date first for chronological scanning */}
                                  <div className="mb-2.5 flex flex-wrap items-center gap-2">
                                    <time
                                      dateTime={entry.date}
                                      title={formatDate(entry.date)}
                                      className="text-[11px] font-medium tracking-tight text-gray-500 dark:text-gray-500"
                                    >
                                      {formatRelative(entry.date)}
                                    </time>
                                    <span
                                      aria-hidden
                                      className="text-gray-300 dark:text-gray-600"
                                    >
                                      ·
                                    </span>
                                    <span
                                      className={`ring-current/10 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] ring-1 ${config.color}`}
                                    >
                                      {config.label}
                                    </span>
                                    {isNew && (
                                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-800/40">
                                        <span className="h-1 w-1 animate-pulse rounded-full bg-rose-500 dark:bg-rose-400" />
                                        جديد
                                      </span>
                                    )}
                                  </div>

                                  {/* Title — editorial scale for readability */}
                                  <h3 className="text-[17px] font-bold leading-snug tracking-tight text-gray-900 dark:text-gray-300">
                                    {entry.title}
                                  </h3>

                                  {/* Description — comfortable reading body */}
                                  <p className="mt-2 text-[14px] leading-[1.7] text-gray-600 dark:text-gray-300">
                                    {entry.description}
                                  </p>

                                  {entry.link && (
                                    <Link
                                      to={entry.link}
                                      onClick={handleClose}
                                      className="group/cta mt-4 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br from-purple-800 to-purple-950 px-4 py-2 text-sm font-semibold text-white dark:text-gray-300 shadow-sm shadow-purple-900/20 ring-1 ring-purple-900/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-purple-900/30 active:translate-y-0 dark:from-purple-600 dark:to-purple-800"
                                    >
                                      جرّب الآن
                                      <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover/cta:-translate-x-0.5 rtl:rotate-180" />
                                    </Link>
                                  )}
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </div>

            <style>{`
              @keyframes whatsNewSlideIn {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center">
    <span className="relative flex h-14 w-14 items-center justify-center">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-br from-purple-200/40 to-transparent blur-xl dark:from-purple-700/30"
      />
      <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-purple-50 text-purple-700 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
        <Icon className="h-6 w-6" />
      </span>
    </span>
    <div>
      <h3 className="text-sm font-bold tracking-tight text-gray-900 dark:text-gray-200">
        {title}
      </h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        {description}
      </p>
    </div>
  </div>
);

export default memo(WhatsNew);
